<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable mb-10"
      :items="tunnelsList"
      v-model:expanded="expanded"
      :fixed-header="true"
      :items-length="total"
      :items-per-page="pagination.pageSize"
      :page="pagination.page + 1"
      :show-select="false"
      item-key="id"
      :headers="headers"
      show-expand
    >
      <!--Slot for table header-->
      <template #headers>
        <tr>
          <th class="w-5 pl-0 pr-0" :aria-label="$t('ariaLabels.expanderColumn')"></th>
          <th class="w-5 pl-0 pr-0" :aria-label="$t('ariaLabels.iconColumn')"></th>
          <th class="pl-0 w-30">
            <span>
              {{ $t(`network.siteToSite.tunnels.table.siteName`) }}
            </span>
          </th>
          <th class="w-30">
            <span>
              {{ $t(`network.siteToSite.tunnels.table.remoteGateway`) }}
            </span>
          </th>
          <th class="w-20">
            <span>
              {{ $t(`network.siteToSite.tunnels.table.status`) }}
            </span>
          </th>
          <th :aria-label="$t('ariaLabels.actionsColumn')"></th>
        </tr>
      </template>
      <!--Slot for rows-->
      <template #item="{ item, index, isExpanded, toggleExpand, internalItem }">
        <tr
          :id="`${index}`"
          :key="index"
          v-intersect="handleVirtualScroll"
          :data-testid="`table-row-${index}`"
          class="side-to-side-table-row"
          :row-disabled="isRowDisabled(item)"
        >
          <td>
            <v-icon
              class="ml-1"
              size="24"
              color="primary"
              :class="{ rotated: isExpanded(internalItem) }"
              :data-testid="`tunnels-expander-btn-${index}`"
              icon="$chevronDown"
              @click="toggleExpand(internalItem)"
            >
            </v-icon>
          </td>
          <td class="pl-2 pr-2" :data-testid="`allow-list-table-name-col-${index}`">
            <v-icon size="43" icon="$globe"></v-icon>
          </td>
          <td class="pl-0">
            <span class="body2 text--semibold">{{ item.siteName }}</span>
          </td>
          <td>
            <span class="body2">{{ item.remoteGatewayIp }}</span>
          </td>
          <td>
            <span :class="getStateClass(item)">
              {{ $t(`network.siteToSite.tunnels.table.${item.status}`) }}
            </span>
          </td>
          <td class="coro-table-col--sticky-right">
            <v-menu bottom left>
              <template v-slot:activator="{ props }">
                <v-icon icon="$dots" class="item-clickable" v-bind="props"></v-icon>
              </template>

              <v-list>
                <v-list-item
                  v-for="tunnelsAction in getActionsList(item)"
                  :key="tunnelsAction"
                  @click="handleItemAction(tunnelsAction, item)"
                >
                  <v-list-item-title :data-testid="`tunnels-table-${tunnelsAction}-action`">
                    {{ $t(`network.siteToSite.tunnels.actions.${tunnelsAction}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
      <template #expanded-row="{ item }">
        <tr
          v-for="server in getUniqueServers(item.servers)"
          :key="server.ip"
          :data-testid="`tunnel-server-row-${server.ip}`"
          :row-disabled="isRowDisabled(item)"
        >
          <td></td>
          <td></td>
          <td class="pl-0">
            <span class="body2">
              {{ server.ip }}
            </span>
          </td>
          <td>
            <span class="body2"> - </span>
          </td>
          <td></td>
          <td></td>
        </tr>
      </template>
      <template v-slot:bottom> </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { storeToRefs } from "pinia";
import uniqBy from "lodash/uniqBy";
import {
  type SiteToSiteTunnel,
  type SiteToSiteTunnelServer,
  SiteToSiteTunnelStatus,
  useSiteToSiteTunnelStore,
} from "@/_store/network/site-to-site-tunnels.module";
import { SubscriptionModule } from "@/constants/workplaces";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import { SiteToSiteTunnelsAction } from "@/constants/network";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default defineComponent({
  components: {
    TableWrapper,
  },
  emits: ["item-action-applied", "last-item-reached"],
  setup(_, { emit }) {
    const i18n = useI18n();
    const expanded = ref([]);
    const siteToSiteTunnelsStore = useSiteToSiteTunnelStore();
    const { tunnelsList, total, pagination } = storeToRefs(siteToSiteTunnelsStore);
    const actionNotAllowed = computed(() => {
      return isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.PROTECTION,
        SubscriptionModule.NETWORK
      );
    });

    const getStateClass = (item: SiteToSiteTunnel) => ({
      "subtitle2 text-green-dark": item.status === SiteToSiteTunnelStatus.CONNECTED,
      "subtitle2 text-red-dark": item.status === SiteToSiteTunnelStatus.DISCONNECTED,
      "body2 text-indigo-medium": item.status === SiteToSiteTunnelStatus.DISABLED,
    });

    const handleItemAction = (action: SiteToSiteTunnelsAction, item: SiteToSiteTunnel) => {
      const payload = {
        item,
        action,
      };
      emit("item-action-applied", payload);
    };

    const getActionsList = (item: SiteToSiteTunnel) => {
      if (item.status === SiteToSiteTunnelStatus.DISABLED) {
        return [
          SiteToSiteTunnelsAction.EDIT,
          SiteToSiteTunnelsAction.ENABLE_TUNNEL,
          SiteToSiteTunnelsAction.DELETE,
        ];
      }
      return [
        SiteToSiteTunnelsAction.EDIT,
        SiteToSiteTunnelsAction.TEST,
        SiteToSiteTunnelsAction.DOWNLOAD_LOG,
        SiteToSiteTunnelsAction.DISABLE_TUNNEL,
        SiteToSiteTunnelsAction.DELETE,
      ];
    };

    const isRowDisabled = (item: SiteToSiteTunnel) => {
      return item.status === SiteToSiteTunnelStatus.DISABLED ? true : null;
    };

    const getUniqueServers = (servers: SiteToSiteTunnelServer[]) => uniqBy(servers ?? [], "ip");

    const { onIntersect: handleVirtualScroll } = useInfiniteScroll(tunnelsList, { total }, () =>
      emit("last-item-reached")
    );

    return {
      expanded,
      tunnelsList,
      total,
      pagination,
      actionNotAllowed,
      getStateClass,
      handleItemAction,
      handleVirtualScroll,
      getActionsList,
      isRowDisabled,
      getUniqueServers,
      headers: [
        {
          title: "",
          key: "data-table-expand",
          sortable: false,
          headerProps: {
            "aria-label": i18n.t("ariaLabels.expanderColumn"),
          },
        },
        {
          title: "",
          key: "icon",
          sortable: false,
          headerProps: {
            "aria-label": i18n.t("ariaLabels.iconColumn"),
          },
        },
        {
          title: i18n.t("network.siteToSite.tunnels.table.siteName"),
          key: "siteName",
          sortable: false,
        },
        {
          title: i18n.t("network.siteToSite.tunnels.table.remoteGateway"),
          key: "remoteGateway",
          sortable: false,
        },
        {
          title: i18n.t("network.siteToSite.tunnels.table.status"),
          key: "workspaceId",
          sortable: false,
        },
        {
          title: "",
          key: "actions",
          sortable: false,
          headerProps: {
            "aria-label": i18n.t("ariaLabels.actionsColumn"),
          },
        },
      ],
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(*) {
  [row-disabled] {
    background-color: rgb(var(--v-theme-gray-faint));
  }
}
</style>
